import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sun, Moon, Monitor, Trash2, Plus, Loader2, Mail } from 'lucide-react';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { settingsApi, accountsApi, type UserSettings } from '@/lib/api/endpoints';
import { AutoresponderCard } from '@/components/settings/AutoresponderCard';
import { SpamShieldCard } from '@/components/settings/SpamShieldCard';
import { ShareManager } from '@/components/settings/ShareManager';
import { SignatureGenerator } from '@/components/settings/SignatureGenerator';
import { SendingServerDialog } from '@/components/settings/SendingServerDialog';
import { useTheme } from '@/stores/theme.store';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api/client';

export function SettingsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setTheme = useTheme((s) => s.setTheme);
  const currentTheme = useTheme((s) => s.theme);

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  const [draft, setDraft] = useState<UserSettings | null>(null);

  useEffect(() => {
    if (settingsQuery.data) setDraft(settingsQuery.data.settings);
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (data: Partial<UserSettings>) => settingsApi.update(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Preferenze salvate');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Errore salvataggio'),
  });

  const removeAccountMutation = useMutation({
    mutationFn: (id: string) => accountsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Account rimosso');
    },
  });

  if (!draft) {
    return (
      <div className="h-screen flex bg-background">
        <InboxSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  function set<K extends keyof UserSettings>(key: K, value: UserSettings[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function handleSave() {
    if (!draft) return;
    saveMutation.mutate(draft);
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-8 space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Impostazioni</h1>
            <p className="text-sm text-muted-foreground">Personalizza la tua esperienza Blagomail.</p>
          </div>

          {/* Tema */}
          <Card>
            <CardHeader>
              <CardTitle>Aspetto</CardTitle>
              <CardDescription>Scegli il tema dell&apos;interfaccia.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => { setTheme('light'); set('theme', 'light'); }}
                  className={cn(
                    'border rounded-lg p-4 flex flex-col items-center gap-2 transition-colors',
                    currentTheme === 'light' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  )}
                >
                  <Sun className="h-6 w-6" />
                  <span className="text-sm font-medium">Chiaro</span>
                </button>
                <button
                  onClick={() => { setTheme('dark'); set('theme', 'dark'); }}
                  className={cn(
                    'border rounded-lg p-4 flex flex-col items-center gap-2 transition-colors',
                    currentTheme === 'dark' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  )}
                >
                  <Moon className="h-6 w-6" />
                  <span className="text-sm font-medium">Scuro</span>
                </button>
                <button
                  onClick={() => { setTheme('system'); set('theme', 'system'); }}
                  className={cn(
                    'border rounded-lg p-4 flex flex-col items-center gap-2 transition-colors',
                    currentTheme === 'system' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  )}
                >
                  <Monitor className="h-6 w-6" />
                  <span className="text-sm font-medium">Sistema</span>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Firma */}
          <Card>
            <CardHeader>
              <CardTitle>Firma email</CardTitle>
              <CardDescription>Aggiunta automaticamente alla fine delle nuove email e risposte.</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={draft.signature_text}
                onChange={(e) => set('signature_text', e.target.value)}
                rows={5}
                placeholder="Cordiali saluti,&#10;Mario Rossi&#10;Blagoweb S.r.l."
              />
              {draft.signature_html ? (
                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Anteprima firma HTML (usata nelle email inviate)</Label>
                    <button type="button" className="text-xs text-muted-foreground hover:text-destructive" onClick={() => set('signature_html', '')}>
                      Rimuovi HTML
                    </button>
                  </div>
                  <div
                    className="mt-1 rounded-md border bg-white p-3 text-sm overflow-x-auto"
                    dangerouslySetInnerHTML={{ __html: draft.signature_html }}
                  />
                </div>
              ) : null}
              <SignatureGenerator
                defaultName={
                  accountsQuery.data?.accounts.find((a) => a.is_default)?.display_name ??
                  accountsQuery.data?.accounts[0]?.display_name ??
                  undefined
                }
                onGenerated={(text, html) => { set('signature_text', text); set('signature_html', html); }}
              />
            </CardContent>
          </Card>

          {/* AI preferences */}
          <Card>
            <CardHeader>
              <CardTitle>AI</CardTitle>
              <CardDescription>Configurazione delle funzioni intelligenti.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tono predefinito Smart Reply</Label>
                <select
                  className="mt-1 w-full border rounded-md bg-background px-3 py-2 text-sm"
                  value={draft.default_reply_tone}
                  onChange={(e) => set('default_reply_tone', e.target.value as UserSettings['default_reply_tone'])}
                >
                  <option value="formale">Formale</option>
                  <option value="amichevole">Amichevole</option>
                  <option value="breve">Breve</option>
                  <option value="dettagliato">Dettagliato</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Filtro spam con AI */}
          <SpamShieldCard />

          {/* Out-of-office / risposta automatica */}
          <AutoresponderCard accounts={accountsQuery.data?.accounts ?? []} />

          {/* Caselle condivise / deleghe */}
          <ShareManager />

          {/* Comportamento */}
          <Card>
            <CardHeader>
              <CardTitle>Comportamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Notifiche browser</Label>
                  <p className="text-xs text-muted-foreground">Mostra notifiche per nuovi messaggi.</p>
                </div>
                <Switch
                  checked={draft.notifications_enabled}
                  onCheckedChange={(v) => {
                    set('notifications_enabled', v);
                    if (v && Notification.permission === 'default') {
                      void Notification.requestPermission();
                    }
                  }}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Scorciatoie da tastiera</Label>
                  <p className="text-xs text-muted-foreground">Premi ? per la guida completa.</p>
                </div>
                <Switch
                  checked={draft.keyboard_shortcuts_enabled}
                  onCheckedChange={(v) => set('keyboard_shortcuts_enabled', v)}
                />
              </div>
              <Separator />
              <div>
                <Label>Auto-salvataggio bozze (secondi)</Label>
                <Input
                  type="number"
                  min={2}
                  max={60}
                  value={draft.auto_save_draft_seconds}
                  onChange={(e) => set('auto_save_draft_seconds', parseInt(e.target.value, 10) || 5)}
                  className="mt-1 w-32"
                />
              </div>
            </CardContent>
          </Card>

          {/* Account */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Account email collegati</CardTitle>
                  <CardDescription>Gestisci le caselle IMAP/SMTP collegate.</CardDescription>
                </div>
                <Button size="sm" onClick={() => navigate('/setup')}>
                  <Plus className="h-4 w-4 mr-1" /> Aggiungi
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(accountsQuery.data?.accounts ?? []).map((a) => (
                  <div key={a.id} className="flex items-center gap-3 border rounded-md p-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{a.email_address}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.imap_host} - {a.is_default ? 'Predefinito' : ''}
                      </div>
                    </div>
                    <SendingServerDialog account={a} />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm(`Rimuovere ${a.email_address}?`)) {
                          removeAccountMutation.mutate(a.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {(accountsQuery.data?.accounts ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Nessun account collegato.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Save bar */}
          <div className="sticky bottom-0 bg-background border-t -mx-8 px-8 py-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate('/')}>Annulla</Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salva preferenze
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
